import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.sort.js";
import React from 'react';
import { InputNumber } from '@isyc-react/form/modules/input/number';
import { InputSelect } from '@isyc-react/form/modules/input/select';
import { InputTextOutlined, InputText } from '@isyc-react/form/modules/input/text';
import { FormSize } from '@isyc-react/form';
import { ColumnFilterContainer, defaultTableFilterType } from "./column-filter-base.component";
import { useTranslation } from 'react-i18next';
import { nstable } from "../../../../i18n/namespaces";
import { renderToString } from 'react-dom/server';
import "./column-filters.scss";

// Define a default UI for filtering
export const DefaultColumnFilter = props => {
  const {
    column,
    defaultFilterType = defaultTableFilterType
  } = props;
  const {
    filterValue,
    preFilteredRows,
    setFilter,
    filterTooltip
  } = column;
  const {
    t
  } = useTranslation(nstable.common);
  return /*#__PURE__*/React.createElement(ColumnFilterContainer, props, defaultFilterType === 'insideLabel' && /*#__PURE__*/React.createElement(InputTextOutlined, {
    value: filterValue || '',
    onChange: change => setFilter(change || undefined),
    placeholder: column.render('Header'),
    size: FormSize.SM
  }), defaultFilterType === 'outsideLabel' && /*#__PURE__*/React.createElement(InputText, {
    value: filterValue || '',
    onChange: change => setFilter(change || undefined),
    placeholder: t('table.header.filter.placeholder'),
    size: FormSize.SM
  }), defaultFilterType === 'placeholderLabel' && /*#__PURE__*/React.createElement(InputText, {
    value: filterValue || '',
    onChange: change => setFilter(change || undefined),
    placeholder: renderToString(column.render('Header')),
    size: FormSize.SM
  }));
};

// TODO add DateColumnFilter

// This is a custom filter UI for selecting
// a unique option from a list
// TODO este solo es válido cuando no hay fetch
// (este automaticamente coge las opciones disponibles desde los datos actuales que hay)
// permitir que se le puedan pasar opciones o algo así, y sobreescriba y evite la lógica previa
export const SelectColumnFilter = props => {
  // Calculate the options for filtering
  // using the preFilteredRows

  const {
    column
  } = props;
  const {
    filterValue,
    preFilteredRows,
    setFilter,
    id,
    filterTooltip
  } = column;

  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach(row => {
      options.add(row.values[id]);
    });
    const optionsArray = [...options.values()].sort((a, b) => ('' + a).localeCompare(b));
    return optionsArray;
  }, [id, preFilteredRows]);
  return /*#__PURE__*/React.createElement(ColumnFilterContainer, props, /*#__PURE__*/React.createElement("div", {
    className: "filter-select-container"
  }, /*#__PURE__*/React.createElement(InputSelect, {
    value: filterValue ? {
      label: filterValue,
      value: filterValue
    } : null,
    onChange: selected => {
      var _selected$value;
      setFilter((_selected$value = selected === null || selected === void 0 ? void 0 : selected.value) !== null && _selected$value !== void 0 ? _selected$value : undefined);
    },
    isClearable: true,
    options: options.map(option => ({
      label: option,
      value: option
    })),
    menuPortalTarget: document.body,
    placeholder: column.render('Header'),
    size: FormSize.MD
  })));
};

// This is a custom filter UI that uses a
// slider to set the filter value between a column's
// min and max values
// TODO este solo es válido cuando no hay fetch
// permitir que se le puedan pasar [min,max], y sobreescriba y evite la lógica previa
export const SliderColumnFilter = props => {
  // Calculate the min and max
  // using the preFilteredRows

  const {
    column
  } = props;
  const {
    filterValue,
    preFilteredRows,
    setFilter,
    id,
    filterTooltip
  } = column;
  const [min, max] = React.useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach(row => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);
  return /*#__PURE__*/React.createElement(ColumnFilterContainer, props, /*#__PURE__*/React.createElement("input", {
    type: "range",
    min: min,
    max: max,
    value: filterValue || min,
    onChange: e => {
      setFilter(parseInt(e.target.value, 10));
    }
  }), /*#__PURE__*/React.createElement("button", {
    onClick: () => setFilter(undefined)
  }, "Off"));
};

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
// TODO este solo es válido cuando no hay fetch
// permitir que se le puedan pasar [min,max], y sobreescriba y evite la lógica previa
export const NumberRangeColumnFilter = props => {
  const {
    column
  } = props;
  const {
    filterValue = [],
    preFilteredRows,
    setFilter,
    id,
    filterTooltip
  } = column;
  const [min, max] = React.useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach(row => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);
  return /*#__PURE__*/React.createElement(ColumnFilterContainer, props, /*#__PURE__*/React.createElement("div", {
    className: "filter-number-range-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "range-min"
  }, /*#__PURE__*/React.createElement(InputNumber, {
    value: filterValue[0] || '',
    onChange: val => {
      setFilter((old = []) => [val, old[1]]);
    },
    allowDecimals: true,
    allowedDecimalSeparators: [',', '.'],
    placeholder: `Min (${min})`
  })), /*#__PURE__*/React.createElement("span", null, "to"), /*#__PURE__*/React.createElement("div", {
    className: "range-max"
  }, /*#__PURE__*/React.createElement(InputNumber, {
    value: filterValue[1] || '',
    onChange: val => {
      setFilter((old = []) => [old[0], val]);
    },
    placeholder: `Max (${max})`,
    allowDecimals: true,
    allowedDecimalSeparators: [',', '.']
  }))));
};